import React, { useState, useEffect, useCallback } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Spinner,
} from 'reactstrap'
import PMBSelect from 'components/PMBSelect'
import { useSnackbar } from 'notistack'
import debounce from 'lodash.debounce'
import {
  useLazyFetchTransferTargetsQuery,
  useTransferStaffMutation,
} from '../farmers-list/farmersListApiSlice'
import CustomButton from 'components/Buttons/CustomButton'

const ModalTransferStaff = ({ isOpen, toggle, selectedRow, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar()

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [options, setOptions] = useState([])
  const [hasMore, setHasMore] = useState(true)
  const [selectedGroup, setSelectedGroup] = useState(null)

  const [fetchTransferTargets, { isFetching }] =
    useLazyFetchTransferTargetsQuery()
  const [transferStaff, { isLoading: isTransferring }] =
    useTransferStaffMutation()

  const userId = selectedRow?.userId

  const loadOptions = async (querySearch, currentPage) => {
    if (!userId) return

    try {
      const res = await fetchTransferTargets({
        userId,
        searchTerm: querySearch,
        sortdir: 'DESC',
        sortby: 'name',
        page: currentPage,
        size: 10,
      }).unwrap()

      const newOptions =
        res.items?.map((item) => ({
          label: item.name,
          value: item.code,
        })) || []

      if (currentPage === 0) {
        setOptions(newOptions)
      } else {
        setOptions((prev) => [...prev, ...newOptions])
      }

      if (currentPage >= res.totalPages - 1 || newOptions.length === 0) {
        setHasMore(false)
      } else {
        setHasMore(true)
      }
    } catch (error) {
      console.error('Error fetching groups', error)
    }
  }

  // Initial load
  useEffect(() => {
    if (isOpen && userId) {
      setSearch('')
      setPage(0)
      setOptions([])
      setSelectedGroup(null)
      setHasMore(true)
      loadOptions('', 0)
    }
  }, [isOpen, userId])

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value)
      setPage(0)
      loadOptions(value, 0)
    }, 500),
    [userId],
  )

  const handleInputChange = (value, { action }) => {
    if (action === 'input-change') {
      debouncedSearch(value)
    }
  }

  const handleMenuScrollToBottom = () => {
    if (!isFetching && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      loadOptions(search, nextPage)
    }
  }

  const handleSubmit = async () => {
    if (!selectedGroup) return

    try {
      await transferStaff({
        userId: userId,
        fromGroupCode: selectedRow?.code,
        toGroupCode: selectedGroup.value,
      }).unwrap()

      enqueueSnackbar('Berhasil memindahkan anggota', { variant: 'success' })
      toggle()
      if (onSuccess) onSuccess()
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || 'Terdapat kesalahan data yang dikirimkan',
        { variant: 'error' },
      )
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered data-testid="farmer-transfer-modal">
      <ModalHeader toggle={toggle}>Transfer Anggota</ModalHeader>
      <ModalBody>
        <p className="mb-4">
          Pilih grup tujuan untuk memindahkan{' '}
          <strong>{selectedRow?.name}</strong> dari grup saat ini.
        </p>

        <FormGroup>
          <Label className="fw-semibold">Grup Tujuan</Label>
          <PMBSelect
            placeholder="Cari grup tujuan..."
            options={options}
            value={selectedGroup}
            onChange={(val) => setSelectedGroup(val)}
            onInputChange={handleInputChange}
            onMenuScrollToBottom={handleMenuScrollToBottom}
            isLoading={isFetching}
            isClearable
            testId="farmer-transfer-group-select"
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <CustomButton
          color="secondary"
          className="btn-cancel"
          outline
          onClick={toggle}
          disabled={isTransferring}
          testId="farmer-transfer-cancel-button"
        >
          Batal
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleSubmit}
          disabled={!selectedGroup || isTransferring}
          testId="farmer-transfer-submit-button"
        >
          {isTransferring ? <Spinner size="sm" /> : 'Transfer'}
        </CustomButton>
      </ModalFooter>
    </Modal>
  )
}

export default ModalTransferStaff
